import { useEffect, useState, type ReactNode } from 'react'
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import { useProjects } from '../lib/queries'
import type { User } from '../lib/types'
import { Avatar, Button, cx, useTheme } from './ui'
import { CommandPalette } from './CommandPalette'
import { NewProjectDialog } from './NewProjectDialog'

export function AppShell({ user, children }: { user: User; children: ReactNode }) {
  const [paletteOpen, setPaletteOpen] = useState(false)
  const [navOpen, setNavOpen] = useState(false)
  const [newProject, setNewProject] = useState(false)
  const location = useLocation()

  // Global shortcuts. `/` is deliberately ignored while typing, or searching
  // would swallow every slash somebody writes in a description.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const typing =
        e.target instanceof HTMLElement &&
        (e.target.isContentEditable ||
          ['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName))

      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setPaletteOpen(true)
        return
      }
      if (e.key === '/' && !typing && !e.metaKey && !e.ctrlKey) {
        e.preventDefault()
        setPaletteOpen(true)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  // Navigating on a phone should close the menu that got you there.
  useEffect(() => setNavOpen(false), [location.pathname])

  return (
    <div className="flex h-full">
      <MobileBar onMenu={() => setNavOpen(true)} onSearch={() => setPaletteOpen(true)} />

      {navOpen && (
        <button
          aria-label="Close menu"
          onClick={() => setNavOpen(false)}
          className="fade-in fixed inset-0 z-30 bg-black/35 md:hidden"
        />
      )}

      <Sidebar
        user={user}
        open={navOpen}
        onSearch={() => setPaletteOpen(true)}
        onNewProject={() => setNewProject(true)}
      />

      <main className="flex min-w-0 flex-1 flex-col pt-12 md:pt-0">{children}</main>

      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
      <NewProjectDialog open={newProject} onOpenChange={setNewProject} />
    </div>
  )
}

function MobileBar({ onMenu, onSearch }: { onMenu: () => void; onSearch: () => void }) {
  return (
    <header className="fixed inset-x-0 top-0 z-20 flex h-12 items-center gap-2 border-b border-[var(--line)] bg-[var(--surface)] px-3 md:hidden">
      <Button variant="ghost" size="icon" onClick={onMenu} aria-label="Open menu">
        <MenuIcon />
      </Button>
      <span className="task-key text-[13px] font-semibold">
        artsol<span className="text-[var(--ink-3)]">/</span>tasks
      </span>
      <Button variant="ghost" size="icon" onClick={onSearch} aria-label="Search" className="ml-auto">
        <SearchIcon />
      </Button>
    </header>
  )
}

function Sidebar({
  user, open, onSearch, onNewProject,
}: {
  user: User
  open: boolean
  onSearch: () => void
  onNewProject: () => void
}) {
  const projects = useProjects()
  const { dark, toggle } = useTheme()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  async function signOut() {
    await api.logout().catch(() => {})
    queryClient.clear()
    navigate('/login', { replace: true })
  }

  const active = projects.data?.filter((p) => !p.archivedAt) ?? []

  return (
    <aside
      className={cx(
        'fixed inset-y-0 left-0 z-40 flex w-[228px] shrink-0 flex-col border-r border-[var(--line)]',
        'bg-[var(--surface)] transition-transform duration-[180ms]',
        'md:static md:translate-x-0',
        open ? 'translate-x-0' : '-translate-x-full',
      )}
    >
      <div className="flex h-12 items-center gap-2 px-3">
        <span aria-hidden className="flex h-6 w-6 items-center justify-center rounded bg-[var(--accent)]">
          <span className="flex items-end gap-[2px]">
            <i className="block h-[11px] w-[2.5px] rounded-[1px] bg-white/55" />
            <i className="block h-[8px] w-[2.5px] rounded-[1px] bg-white" />
            <i className="block h-[5px] w-[2.5px] rounded-[1px] bg-white/75" />
          </span>
        </span>
        <span className="task-key text-[13px] font-semibold tracking-tight">
          artsol<span className="text-[var(--ink-3)]">/</span>tasks
        </span>
      </div>

      <div className="px-2.5 pb-2">
        <button
          onClick={onSearch}
          className={cx(
            'flex w-full items-center gap-2 rounded-md border border-[var(--line)] bg-[var(--sunken)]',
            'px-2 py-1.5 text-[13px] text-[var(--ink-3)] transition-colors duration-[140ms]',
            'hover:border-[var(--line-strong)] hover:text-[var(--ink-2)]',
          )}
        >
          <SearchIcon />
          <span>Search</span>
          <kbd className="task-key ml-auto rounded border border-[var(--line)] px-1 text-[10px] text-[var(--ink-3)]">
            ⌘K
          </kbd>
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto px-2.5 pb-3">
        <RailLink to="/my" icon={<InboxIcon />}>
          My tasks
        </RailLink>

        <div className="mt-5 flex items-center justify-between px-2 pb-1">
          <span className="text-[11px] font-medium uppercase tracking-wider text-[var(--ink-3)]">
            Projects
          </span>
          <button
            onClick={onNewProject}
            aria-label="New project"
            className="text-[var(--ink-3)] transition-colors duration-[140ms] hover:text-[var(--ink)]"
          >
            <PlusIcon />
          </button>
        </div>

        {active.length === 0 && !projects.isLoading && (
          <p className="px-2 py-1.5 text-[12px] leading-relaxed text-[var(--ink-3)]">
            No projects yet.{' '}
            <button onClick={onNewProject} className="text-[var(--accent)] hover:underline">
              Create one
            </button>
            .
          </p>
        )}

        {/* Sub-projects sit under their parent, so "shop" reads as one thing
            with services beneath it rather than four unrelated boards. */}
        {active
          .filter((p) => !p.parentId)
          .map((parent) => {
            const children = active.filter((c) => c.parentId === parent.id)
            return (
              <div key={parent.id}>
                <RailLink
                  to={`/p/${parent.key}`}
                  icon={
                    <span
                      aria-hidden
                      className="h-2 w-2 shrink-0 rounded-[3px]"
                      style={{ background: parent.color }}
                    />
                  }
                  trailing={
                    parent.openTasks > 0 ? (
                      <span className="task-key text-[11px] text-[var(--ink-3)]">
                        {parent.openTasks}
                      </span>
                    ) : null
                  }
                >
                  {parent.name}
                </RailLink>

                {children.length > 0 && (
                  <div className="ml-[13px] border-l border-[var(--line)] pl-2">
                    {children.map((child) => (
                      <RailLink
                        key={child.id}
                        to={`/p/${child.key}`}
                        icon={
                          <span
                            aria-hidden
                            className="h-1.5 w-1.5 shrink-0 rounded-full"
                            style={{ background: child.color }}
                          />
                        }
                        trailing={
                          child.openTasks > 0 ? (
                            <span className="task-key text-[11px] text-[var(--ink-3)]">
                              {child.openTasks}
                            </span>
                          ) : null
                        }
                      >
                        {child.name}
                      </RailLink>
                    ))}
                  </div>
                )}
              </div>
            )
          })}

        {/* A sub-project whose parent is archived would otherwise vanish. */}
        {active
          .filter((p) => p.parentId && !active.some((c) => c.id === p.parentId))
          .map((orphan) => (
            <RailLink
              key={orphan.id}
              to={`/p/${orphan.key}`}
              icon={
                <span
                  aria-hidden
                  className="h-2 w-2 shrink-0 rounded-[3px]"
                  style={{ background: orphan.color }}
                />
              }
            >
              {orphan.name}
            </RailLink>
          ))}
      </nav>

      <div className="border-t border-[var(--line)] p-2.5">
        <div className="flex items-center gap-2">
          <Avatar user={{ ...user, avatarUrl: user.avatarUrl }} size={24} />
          <div className="min-w-0 flex-1">
            <p className="truncate text-[12.5px] font-medium leading-tight">{user.name}</p>
            <p className="truncate text-[11px] leading-tight text-[var(--ink-3)]">{user.email}</p>
          </div>
          <button
            onClick={toggle}
            aria-label={dark ? 'Switch to light theme' : 'Switch to dark theme'}
            className="rounded p-1 text-[var(--ink-3)] transition-colors duration-[140ms] hover:bg-[var(--sunken)] hover:text-[var(--ink)]"
          >
            {dark ? <SunIcon /> : <MoonIcon />}
          </button>
        </div>

        <div className="mt-2 flex items-center gap-1 text-[12px]">
          <Link
            to="/settings"
            className="rounded px-1.5 py-1 text-[var(--ink-3)] transition-colors duration-[140ms] hover:bg-[var(--sunken)] hover:text-[var(--ink)]"
          >
            Settings
          </Link>
          {user.role === 'admin' && (
            <Link
              to="/admin"
              className="rounded px-1.5 py-1 text-[var(--ink-3)] transition-colors duration-[140ms] hover:bg-[var(--sunken)] hover:text-[var(--ink)]"
            >
              Admin
            </Link>
          )}
          <button
            onClick={signOut}
            className="ml-auto rounded px-1.5 py-1 text-[var(--ink-3)] transition-colors duration-[140ms] hover:bg-[var(--sunken)] hover:text-[var(--ink)]"
          >
            Sign out
          </button>
        </div>
      </div>
    </aside>
  )
}

function RailLink({
  to, icon, trailing, children,
}: {
  to: string
  icon: ReactNode
  trailing?: ReactNode
  children: ReactNode
}) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        cx(
          'flex items-center gap-2 rounded-md px-2 py-[5px] text-[13px] transition-colors duration-[140ms]',
          isActive
            ? 'bg-[var(--accent-soft)] font-medium text-[var(--accent)]'
            : 'text-[var(--ink-2)] hover:bg-[var(--sunken)] hover:text-[var(--ink)]',
        )
      }
    >
      <span className="flex w-4 shrink-0 items-center justify-center">{icon}</span>
      <span className="truncate">{children}</span>
      {trailing && <span className="ml-auto">{trailing}</span>}
    </NavLink>
  )
}

// Icons are inline SVG at a shared 14px stroke weight — an icon font or an
// icon package would be more bytes than the eleven glyphs this app uses.
const stroke = {
  width: 14, height: 14, viewBox: '0 0 16 16', fill: 'none',
  stroke: 'currentColor', strokeWidth: 1.5,
  strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const,
}

export function SearchIcon() {
  return (
    <svg {...stroke} aria-hidden>
      <circle cx="7" cy="7" r="4.25" />
      <path d="M10.2 10.2 13.5 13.5" />
    </svg>
  )
}

function MenuIcon() {
  return (
    <svg {...stroke} aria-hidden>
      <path d="M2.5 4h11M2.5 8h11M2.5 12h11" />
    </svg>
  )
}

function PlusIcon() {
  return (
    <svg {...stroke} aria-hidden>
      <path d="M8 3.5v9M3.5 8h9" />
    </svg>
  )
}

function InboxIcon() {
  return (
    <svg {...stroke} aria-hidden>
      <path d="M2 9.5 3.6 3.4A1 1 0 0 1 4.57 2.6h6.86a1 1 0 0 1 .97.8L14 9.5" />
      <path d="M2 9.5h3.2l.8 1.6h4l.8-1.6H14v2.4a1.5 1.5 0 0 1-1.5 1.5h-9A1.5 1.5 0 0 1 2 11.9Z" />
    </svg>
  )
}

function SunIcon() {
  return (
    <svg {...stroke} aria-hidden>
      <circle cx="8" cy="8" r="3" />
      <path d="M8 1.5v1.2M8 13.3v1.2M1.5 8h1.2M13.3 8h1.2M3.4 3.4l.85.85M11.75 11.75l.85.85M12.6 3.4l-.85.85M4.25 11.75l-.85.85" />
    </svg>
  )
}

function MoonIcon() {
  return (
    <svg {...stroke} aria-hidden>
      <path d="M13.2 9.6A5.6 5.6 0 0 1 6.4 2.8a5.6 5.6 0 1 0 6.8 6.8Z" />
    </svg>
  )
}
